import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, AlertCircle, Mic } from 'lucide-react';

const BAR_COUNT = 34;

const VoiceMessagePlayer = ({ audioUrl, duration, sent, c, theme, compact = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [error, setError] = useState(false);
  const [loadedDuration, setLoadedDuration] = useState(0);
  const audioRef = useRef(null);

  // Deterministic pseudo-random waveform seeded from the url so each voice
  // note keeps the same shape across renders (real waveform data isn't stored).
  const bars = useMemo(() => {
    const s = String(audioUrl || '');
    let seed = 7;
    for (let i = 0; i < s.length; i++) seed = (seed * 31 + s.charCodeAt(i)) >>> 0;
    return Array.from({ length: BAR_COUNT }, () => {
      seed = (seed * 1103515245 + 12345) >>> 0;
      return 5 + (seed % 17);
    });
  }, [audioUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setError(false);
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            console.error('Audio playback failed:', err);
            setError(true);
            setIsPlaying(false);
          });
      } else {
        setIsPlaying(true);
      }
    }
  };

  const changeSpeed = () => {
    const speeds = [1, 1.5, 2];
    const nextIndex = (speeds.indexOf(playbackRate) + 1) % speeds.length;
    setPlaybackRate(speeds[nextIndex]);
  };

  const formatTime = (seconds) => {
    if (!seconds || !Number.isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const metaDuration = audioRef.current?.duration;
  const displayDuration =
    (Number.isFinite(loadedDuration) && loadedDuration > 0 ? loadedDuration : 0) ||
    duration ||
    (Number.isFinite(metaDuration) ? metaDuration : 0) ||
    0;
  const progressFraction = displayDuration > 0 ? Math.min(1, currentTime / displayDuration) : 0;

  const handleSeek = (e) => {
    if (!audioRef.current || !displayDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const seekTime = percentage * displayDuration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const isDark = theme === 'dark';
  const activeColor = sent ? '#ffffff' : '#2e7cba';
  const inactiveColor = sent
    ? 'rgba(255,255,255,0.35)'
    : (isDark ? 'rgba(255,255,255,0.28)' : 'rgba(15,23,42,0.18)');
  const textColor = sent ? 'rgba(255,255,255,0.92)' : (c.textSecondary || '#64748b');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? '8px' : '12px',
        padding: compact ? '8px 10px' : '10px 14px',
        background: sent ? 'rgba(255,255,255,0.12)' : (isDark ? 'rgba(255,255,255,0.06)' : '#ffffff'),
        borderRadius: '18px',
        minWidth: compact ? '180px' : '250px',
        maxWidth: compact ? '230px' : '320px',
        border: sent ? '1px solid rgba(255,255,255,0.18)' : `1px solid ${c.border || 'rgba(0,0,0,0.08)'}`,
        boxShadow: sent ? 'none' : '0 2px 10px rgba(0,0,0,0.05)',
        direction: 'ltr',
      }}
    >
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.target.duration) && e.target.duration > 0) {
            setLoadedDuration(e.target.duration);
          }
        }}
      />

      {/* Play / Pause */}
      <button
        onClick={togglePlay}
        disabled={error}
        style={{
          width: compact ? '34px' : '40px',
          height: compact ? '34px' : '40px',
          borderRadius: '50%',
          border: 'none',
          background: sent ? '#ffffff' : 'linear-gradient(135deg, #2e7cba, #1a6baa)',
          color: sent ? '#2e7cba' : '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: error ? 'not-allowed' : 'pointer',
          flexShrink: 0,
          boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
          transition: 'transform 0.1s ease',
          opacity: error ? 0.6 : 1,
        }}
        onMouseDown={(e) => { if (!error) e.currentTarget.style.transform = 'scale(0.94)'; }}
        onMouseUp={(e) => { if (!error) e.currentTarget.style.transform = 'scale(1)'; }}
        onMouseLeave={(e) => { if (!error) e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {error ? (
          <AlertCircle size={compact ? 16 : 18} />
        ) : isPlaying ? (
          <Pause size={compact ? 16 : 18} fill="currentColor" />
        ) : (
          <Play size={compact ? 16 : 18} fill="currentColor" style={{ marginLeft: '2px' }} />
        )}
      </button>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
        {error && (
          <div style={{ fontSize: '11px', color: sent ? '#fff' : '#ef4444', fontWeight: '600' }}>
            Unsupported Format
          </div>
        )}

        {/* Waveform */}
        <div
          onClick={handleSeek}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? '1.5px' : '2.5px',
            height: compact ? '22px' : '28px',
            cursor: 'pointer',
            width: '100%',
          }}
          title="Seek"
        >
          {bars.map((h, i) => {
            const active = (i + 0.5) / bars.length <= progressFraction;
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  minWidth: compact ? '1px' : '2px',
                  maxWidth: compact ? '2px' : '4px',
                  height: `${compact ? Math.max(2, h * 0.7) : h}px`,
                  borderRadius: '2px',
                  background: active ? activeColor : inactiveColor,
                  transition: 'background 0.12s linear',
                }}
              />
            );
          })}
        </div>

        {/* Time + speed */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '11px',
            fontWeight: '600',
            color: textColor,
          }}
        >
          <span>
            {isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(displayDuration)}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Mic size={12} style={{ opacity: 0.75 }} />
            {!error && (
              <button
                onClick={changeSpeed}
                style={{
                  padding: '2px 8px',
                  borderRadius: '9px',
                  border: 'none',
                  background: sent ? 'rgba(255,255,255,0.22)' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'),
                  color: 'inherit',
                  fontSize: '10px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {playbackRate}x
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceMessagePlayer;
